<template>
  <v-card outlined class="group-compare-table">
    <div class="group-compare-table__header">
      <div class="group-compare-table__title">
        <v-icon small class="mr-2">mdi-compare-horizontal</v-icon>
        Compare groups
        <span class="caption grey--text ml-2">Common labelled videos only · B minus A</span>
      </div>
      <v-spacer />
      <v-chip
        v-if="comparison"
        x-small
        :color="comparison.same_series ? 'success' : 'warning'"
        dark
        label
        class="group-compare-table__series"
        :title="
          comparison.same_series
            ? 'Both groups share engines and configuration series'
            : 'The groups use different engine versions or configurations; deltas mix series'
        "
      >
        {{ comparison.same_series ? 'Same series' : 'Different series' }}
      </v-chip>
      <v-btn icon small class="ml-1" title="Close comparison" @click="$emit('close')">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-skeleton-loader v-if="loading" type="table-row@4" class="mx-2" />

    <v-alert v-else-if="error" type="error" outlined dense class="mx-4 my-3">
      {{ error }}
    </v-alert>

    <template v-else-if="comparison">
      <div class="group-compare-table__meta caption grey--text text--darken-1">
        <span><strong>A</strong> {{ nameOf('a') }}</span>
        <span class="mx-2">·</span>
        <span><strong>B</strong> {{ nameOf('b') }}</span>
        <span class="mx-2">·</span>
        <span>{{ comparison.common }} common videos</span>
        <span v-if="onlyInA.length" class="ml-2">· {{ onlyInA.length }} only in A</span>
        <span v-if="onlyInB.length" class="ml-2">· {{ onlyInB.length }} only in B</span>
      </div>

      <v-simple-table dense class="group-compare-table__table">
        <template #default>
          <thead>
            <tr>
              <th>Method</th>
              <th>Metric</th>
              <th class="text-right">A</th>
              <th class="text-right">B</th>
              <th class="text-right">Delta</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="method in methods">
              <tr
                v-for="(metric, index) in metrics"
                :key="`${method.key}-${metric.key}`"
                class="group-compare-table__row"
              >
                <td
                  v-if="index === 0"
                  :rowspan="metrics.length"
                  class="group-compare-table__method"
                >
                  {{ method.label }}
                </td>
                <td>{{ metric.label }}</td>
                <td
                  class="text-right"
                  :class="`${colorFor(metric.key, valueOf('a', method.key, metric.key))}--text`"
                >
                  {{ formatPct(valueOf('a', method.key, metric.key)) }}
                </td>
                <td
                  class="text-right"
                  :class="`${colorFor(metric.key, valueOf('b', method.key, metric.key))}--text`"
                >
                  {{ formatPct(valueOf('b', method.key, metric.key)) }}
                </td>
                <td
                  class="text-right group-compare-table__delta"
                  :class="deltaClass(method.key, metric)"
                >
                  {{ deltaText(method.key, metric) }}
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>

      <div v-if="onlyInA.length || onlyInB.length" class="group-compare-table__only">
        <div v-if="onlyInA.length" class="caption">
          <strong>Only in A:</strong> {{ onlyInA.map(memberLabel).join(', ') }}
        </div>
        <div v-if="onlyInB.length" class="caption">
          <strong>Only in B:</strong> {{ onlyInB.map(memberLabel).join(', ') }}
        </div>
      </div>
    </template>
  </v-card>
</template>

<script>
import { memberLabel } from '@/utils/groupResolution';
import {
  SERIES_METRICS,
  formatDelta,
  metricStatus,
  metricStatusColor,
} from '@/utils/metricsSeries';

const METHODS = [
  { key: 'computed_subtraction', label: 'Alerts Subtraction' },
  { key: 'alert_major', label: 'Alert Major' },
];

/**
 * Two groups side by side (M4 contract 6, `GET /analysis-groups/compare`): the three PO metrics
 * per method for A and B, the B − A delta coloured by whether it moves toward the target, the
 * same-series flag and the members present in only one of the groups.
 */
export default {
  name: 'GroupCompareTable',

  props: {
    /** `{ a: metrics, b: metrics, deltas, same_series, only_in_a, only_in_b, common }` */
    comparison: { type: Object, default: null },
    /** `{ a: { name }, b: { name } }` display names (the payload only carries ids). */
    names: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },

  data() {
    return { methods: METHODS, metrics: SERIES_METRICS };
  },

  computed: {
    onlyInA() {
      return Array.isArray(this.comparison?.only_in_a) ? this.comparison.only_in_a : [];
    },
    onlyInB() {
      return Array.isArray(this.comparison?.only_in_b) ? this.comparison.only_in_b : [];
    },
  },

  methods: {
    memberLabel,

    nameOf(side) {
      const fromNames = this.names && this.names[side] && this.names[side].name;
      if (fromNames) return fromNames;
      const scope = this.comparison?.[side]?.scope;
      return scope && scope.group_id != null ? `group #${scope.group_id}` : side.toUpperCase();
    },

    valueOf(side, methodKey, metricKey) {
      const metric = SERIES_METRICS.find((m) => m.key === metricKey);
      const block = this.comparison?.[side]?.global?.methods?.[methodKey];
      const value = block && metric ? block[metric.field] : null;
      return value === undefined ? null : value;
    },

    /** Server delta when provided (`deltas[method][metric]`), else B − A computed here. */
    deltaValue(methodKey, metric) {
      const provided = this.comparison?.deltas?.[methodKey]?.[metric.key];
      if (provided !== undefined && provided !== null && Number.isFinite(Number(provided))) {
        return Number(provided);
      }
      const a = this.valueOf('a', methodKey, metric.key);
      const b = this.valueOf('b', methodKey, metric.key);
      if (a === null || b === null) return null;
      return Math.round((b - a) * 10) / 10;
    },

    deltaText(methodKey, metric) {
      const delta = this.deltaValue(methodKey, metric);
      return delta === null ? '—' : formatDelta(0, delta);
    },

    deltaClass(methodKey, metric) {
      const delta = this.deltaValue(methodKey, metric);
      if (delta === null || delta === 0) return '';
      const improves = metric.direction === 'max' ? delta < 0 : delta > 0;
      return improves ? 'success--text' : 'error--text';
    },

    colorFor(metricKey, value) {
      return metricStatusColor(metricStatus(metricKey, value));
    },

    formatPct(value) {
      return value === null || value === undefined ? '—' : `${value}%`;
    },
  },
};
</script>

<style scoped>
.group-compare-table {
  border-radius: 10px !important;
  margin-bottom: 32px;
}

.group-compare-table__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px 4px;
}

.group-compare-table__title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #555;
}

.group-compare-table__meta {
  padding: 4px 16px 8px;
}

.group-compare-table__table {
  background: transparent !important;
}

.group-compare-table__method {
  font-weight: 700;
  vertical-align: top;
}

.group-compare-table__delta {
  font-weight: 700;
}

.group-compare-table__only {
  padding: 8px 16px 12px;
}
</style>
